import { Injector, ValueProvider } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { AND_OPERATOR, DYNAMIC_MATCHERS, DynamicFormControlCondition, DynamicFormControlMatcher, DynamicFormControlModel, DynamicFormControlRelation, DynamicFormGroupModel, DynamicFormRelationService, DynamicSelectModel, OR_OPERATOR } from '@ng-dynamic-forms/core';
import { arrayContentsEqual } from '../helpers/array-contents-equal.helper';

export const MATCH_SELECT_VISIBLE = 'SELECT_VISIBLE';
export const MATCH_SELECT_HIDDEN = 'SELECT_HIDDEN';

/**
 * This matcher re-implements some parts of the dynamic form relation service,
 * in order to allow relating to selects that allow multiple selections (multiple = true).
 *
 * If the select model allows multiple values (multiple: true), matching occurs as follows:
 * If condition.value is an array -> check that condition values exactly match model values (ignoring sequence).
 * If condition.value is not an array -> check that model values include condition value.
 *
 * Additionally this matcher uses the compareWithFn of the related model to determine matches instead of a
 * simple equals check, to enable more flexible matching of options.
 *
 * !!! Note: Currently only works when condition specifies id of a control in the same group. !!!
 */
export const SelectVisibleMatcher: DynamicFormControlMatcher = {
  match:         MATCH_SELECT_VISIBLE,
  opposingMatch: MATCH_SELECT_HIDDEN,
  onChange(hasMatch, model: DynamicFormControlModel, control: UntypedFormControl, injector: Injector) {
    const relationService: DynamicFormRelationService = injector.get(DynamicFormRelationService);
    const relatedFormModels: Record<string, DynamicFormControlModel> = getRelatedFormModels(model);
    const relation: DynamicFormControlRelation = relationService.findRelationByMatcher(model.relations, SelectVisibleMatcher);
    const isMatch = relation && matchesCondition(relation, relatedFormModels, SelectVisibleMatcher);

    model.hidden = !isMatch;
  }
};

export const SELECT_VISIBLE_MATCHER_PROVIDER: ValueProvider = {
  provide:  DYNAMIC_MATCHERS,
  useValue: SelectVisibleMatcher,
  multi:    true
};

// code copied/modified from ng-dynamic-forms, in order to get related form models (as opposed to FormControls)
function getRelatedFormModels(model: DynamicFormControlModel): Record<string, DynamicFormControlModel> {
  const conditionReducer = (models, condition) => {
    const path = condition.id;

    if (!models.hasOwnProperty(path)) {
      const relatedModel =
        model.parent instanceof DynamicFormGroupModel ? model.parent.group.find(child => child.id === condition.id) : null;

      if (relatedModel instanceof DynamicFormControlModel) {
        models[path] = relatedModel;
      } else {
        console.warn(`No related form control with id ${condition.id} could be found`);
      }
    }

    return models;
  };

  const relationReducer = (controls, relation) => relation.when.reduce(conditionReducer, controls);

  return model.relations.reduce(relationReducer, {});
}

// code copied/modified from ng-dynamic-forms, in order to use matches() function
function matchesCondition(
  relation: DynamicFormControlRelation,
  relatedFormModels: Record<string, DynamicFormControlModel>,
  matcher: DynamicFormControlMatcher
): boolean {
  const operator = relation.operator || OR_OPERATOR;

  return relation.when.reduce((hasAlreadyMatched, condition, index) => {
    const path = condition.rootPath || condition.id;

    let relatedFormModel;

    for (const [key, model] of Object.entries(relatedFormModels)) {
      if (key === path) {
        relatedFormModel = model;
        break;
      }
    }

    if (relatedFormModel && relation.match === matcher.match) {
      if (index > 0 && operator === AND_OPERATOR && !hasAlreadyMatched) {
        return false;
      }

      if (index > 0 && operator === OR_OPERATOR && hasAlreadyMatched) {
        return true;
      }

      return matches(relatedFormModel, condition);
    }

    if (relatedFormModel && relation.match === matcher.opposingMatch) {
      if (index > 0 && operator === AND_OPERATOR && hasAlreadyMatched) {
        return true;
      }

      if (index > 0 && operator === OR_OPERATOR && !hasAlreadyMatched) {
        return false;
      }

      return !matches(relatedFormModel, condition);
    }

    return false;
  }, false);
}

// this is the most important part of the code, that checks whether there is a match
function matches(model: DynamicSelectModel<any>, condition: DynamicFormControlCondition) {
  const matchFn = typeof model.compareWithFn === 'function' ? model.compareWithFn : (a, b) => a === b;

  if (model.multiple && Array.isArray(model.value)) {
    // if condition value is an array
    if (Array.isArray(condition.value)) {
      // do an exact check on the contents of the model value
      return arrayContentsEqual(matchFn, model.value, condition.value);
    }
    // otherwise check if model value includes condition value
    return model.value.some(val => matchFn(val, condition.value));
  }

  // if it's not a multiple select, do a normal comparison
  return matchFn(model.value, condition.value);
}
